import { AllocatedArea, Area } from "@types";
import { ShowingGroupMode, PriceStatus } from "@utils/enums";
import { groupBy } from "lodash";
import { AreaFormatted } from "../types";

function isAllocatedArea(obj: any): obj is AllocatedArea {
  return (
    obj.area !== undefined &&
    obj.pricesection !== undefined &&
    obj?.gaarea === undefined
  );
}

export const setAreas = (
  availableAreas: Area,
  isPricesectionEqual: boolean
) => {
  if (!availableAreas) return [];

  const combinedArray = [...availableAreas.ga, ...availableAreas.allocated];
  let groups: Record<string, Array<AreaFormatted>> = {};

  const allocated_areas_obj = groupBy(
    availableAreas.allocated.filter((area) => !area.pricesection.groupkey),
    "area.name"
  );
  const ga_areas_obj = groupBy(
    availableAreas.ga.filter((area) => !area.pricesection.groupkey),
    "area.name"
  );

  const allocated_areas: AreaFormatted[] =
    availableAreas.groupMode === ShowingGroupMode.GROUPED
      ? Object.keys(allocated_areas_obj).map((area_key) => ({
          id: allocated_areas_obj[area_key][0].area.id,
          areaIds: [allocated_areas_obj[area_key][0].area.id],
          venueAreaIds: allocated_areas_obj[area_key].map((a) => a.area.id),
          name: area_key,
          priceSections: allocated_areas_obj[area_key].map(
            (a) => a.pricesection
          ),
          type: "allocated",
          order: allocated_areas_obj[area_key].reduce(
            (prev, curr) => (curr.order < prev ? curr.order : prev),
            999999
          ),
          status: PriceStatus.AVAILABLE,
        }))
      : availableAreas.allocated.map((area) => ({
          id: area.pricesection.id,
          areaIds: [area.area.id],
          venueAreaIds: [area.area.id],
          name: `${
            area.area.name.toLowerCase() !== "general" &&
            area.area.name.toLowerCase() !== "allocated"
              ? area.area.name
              : ""
          } ${isPricesectionEqual ? "" : area.pricesection.name}`.trim(),
          priceSections: [area.pricesection],
          type: "allocated",
          order: area.order,
          pricesectionorder: area.pricesectionorder,
          status: PriceStatus.AVAILABLE,
        }));
  const ga_areas: AreaFormatted[] =
    availableAreas.groupMode === ShowingGroupMode.GROUPED
      ? Object.keys(ga_areas_obj).map((area_key) => ({
          id: ga_areas_obj[area_key][0].area.id,
          areaIds: ga_areas_obj[area_key].map((area) => area.gaarea.id),
          venueAreaIds: ga_areas_obj[area_key].map((area) => area.area.id),
          name: area_key,
          priceSections: ga_areas_obj[area_key].map((a) => a.pricesection),
          type: "ga",
          order: ga_areas_obj[area_key].reduce(
            (prev, curr) => (curr.order < prev ? curr.order : prev),
            999999
          ),
          status: PriceStatus.AVAILABLE,
        }))
      : availableAreas.ga.map((area) => ({
          id: area.pricesection.id,
          areaIds: [area.gaarea.id],
          venueAreaIds: [area.area.id],
          name: `${
            !area.pricesection.name.trim() ||
            (area.gaarea.name !== "GA" &&
              area.gaarea.name !== area.pricesection.name)
              ? `${
                  area.gaarea.name === "GA"
                    ? "General Admission"
                    : area.gaarea.name
                }`
              : ""
          } ${isPricesectionEqual ? "" : area.pricesection.name}`,
          priceSections: [area.pricesection],
          type: "ga",
          order: area.order,
          status: area.pricesection.status,
        }));

  const ungrouped = [...allocated_areas, ...ga_areas];

  combinedArray.forEach((area) => {
    if (!area.pricesection.groupkey) return;
    if (groups[area.pricesection.groupkey]) {
      groups[area.pricesection.groupkey].push(
        isAllocatedArea(area)
          ? {
              id: area.pricesection.id,
              areaIds: [area.area.id],
              venueAreaIds: [area.area.id],
              name: area.pricesection.name,
              priceSections: [area.pricesection],
              type: "allocated",
              order: area.order,
              pricesectionorder: area.pricesectionorder,
              status: PriceStatus.AVAILABLE,
            }
          : {
              id: area.pricesection.id,
              areaIds: [area.gaarea.id],
              venueAreaIds: [area.area.id],
              name: area.gaarea.name,
              priceSections: [area.pricesection],
              type: "ga",
              order: area.order,
              status: area.pricesection.status,
            }
      );
      return;
    }

    groups = {
      ...groups,
      [area.pricesection.groupkey]: [
        isAllocatedArea(area)
          ? {
              id: area.pricesection.id,
              areaIds: [area.area.id],
              venueAreaIds: [area.area.id],
              name: area.pricesection.name,
              priceSections: [area.pricesection],
              type: "allocated",
              order: area.order,
              status: PriceStatus.AVAILABLE,
            }
          : {
              id: area.pricesection.id,
              areaIds: [area.gaarea.id],
              venueAreaIds: [area.area.id],
              name: area.gaarea.name,
              priceSections: [area.pricesection],
              type: "ga",
              order: area.order,
              status: area.pricesection.status,
            },
      ],
    };
  });

  const combined_groups: AreaFormatted[] = Object.keys(groups).map((group) => ({
    id: groups[group].reduce(
      (prev, area) => prev + area.priceSections[0].id,
      0
    ),
    areaIds: groups[group].map((area) => area.areaIds[0]),
    venueAreaIds: groups[group].flatMap((area) => area.venueAreaIds),
    name: group,
    priceSections: groups[group].map((area) => area.priceSections[0]),
    type: "ga",
    order: groups[group].reduce(
      (prev, area) => (area.order < prev ? area.order : prev),
      99999999999
    ),
    status: groups[group].reduce(
      (prev, area) => (area.status > prev ? area.status : prev),
      0
    ),
  }));

  return [...ungrouped, ...combined_groups].sort((a, b) =>
    a.order === b.order && a.pricesectionorder && b.pricesectionorder
      ? a.pricesectionorder - b.pricesectionorder
      : a.order - b.order
  );
};
